import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface Reread {
  id: string;
  reread_at: string | null;
  notes: string | null;
  created_at: string;
}

interface UseBookRereadsResult {
  rereads: Reread[];
  rereadCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useBookRereads(bookId: string): UseBookRereadsResult {
  const { user } = useAuth();
  const [rereads, setRereads] = useState<Reread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRereads = async () => {
    if (!user || !bookId) {
      setRereads([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('user_book_rereads')
        .select('id, reread_at, notes, created_at')
        .eq('user_id', user.id)
        .eq('book_id', bookId)
        .order('reread_at', { ascending: false, nullsFirst: false });

      if (fetchError) throw fetchError;

      setRereads(data || []);
    } catch (err) {
      console.error('Error fetching rereads:', err);
      setError('Could not load rereads');
      setRereads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRereads();
  }, [user?.id, bookId]);

  return {
    rereads,
    rereadCount: rereads.length,
    loading,
    error,
    refresh: fetchRereads
  };
}
